import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Commentable } from 'Types/enums';

import ConfirmationDialog from '../../components/DealGPT/PhaseTwo/ConfirmationDialog';
import { useAutoScrollHighlight } from '../../hooks/useAutoScrollHighlight';
import { createComment, deleteComment, updateComment } from '../../services/comments';
import { ThreadedComment } from '../../Types/comment';
import { formatDate } from '../../util/dateUtils';
import { useAuth } from '../Providers/AuthProvider';
import { CommentAvatar } from './CommentAvatar';

interface CommentProps {
  comment: ThreadedComment;
  entityId: string;
  entityType: Commentable;
  autoScroll?: boolean;
  onScrolled?: () => void;
  refetchComments: () => Promise<void>;
  setParentIsReplying?: React.Dispatch<React.SetStateAction<boolean>>;
  depth?: number;
}

const Comment: React.FC<CommentProps> = ({
  comment,
  entityId,
  entityType,
  autoScroll,
  onScrolled,
  refetchComments,
  setParentIsReplying,
  depth = 0,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(comment.content);
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { currentUser, isAdmin } = useAuth();

  const { ref } = useAutoScrollHighlight({ autoScroll, onScrolled });

  const handleUpdate = useCallback(async () => {
    if (!editedContent.trim()) return;
    await updateComment({ id: comment.id, content: editedContent.trim() });
    setIsEditing(false);
    await refetchComments();
  }, [editedContent, comment.id, refetchComments]);

  const handleDelete = useCallback(async () => {
    await deleteComment(comment.id);
    await refetchComments();
  }, [comment.id, refetchComments]);

  const getRootParentId = useCallback((comment: ThreadedComment, depth: number): string => {
    return depth > 0 && comment.parentId ? comment.parentId : comment.id;
  }, []);

  const handleReply = useCallback(async () => {
    if (!replyContent.trim()) return;
    const parentId = getRootParentId(comment, depth);
    await createComment({
      entityId,
      entityType,
      content: replyContent.trim(),
      parentId,
    });
    setReplyContent('');
    setIsReplying(false);
    await refetchComments();
  }, [replyContent, comment, depth, entityId, entityType, getRootParentId, refetchComments]);

  const formattedCreatedAt = useMemo(() => {
    const datePart = formatDate(comment.createdAt.toString(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const timePart = formatDate(comment.createdAt.toString(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} at ${timePart}`;
  }, [comment.createdAt]);

  return (
    <Box ref={ref} sx={{ pl: depth * 4, mt: depth > 0 ? 2 : 0 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <CommentAvatar comment={comment} />
        <Box flexGrow={1}>
          <Typography variant="subtitle2">{comment.createdByName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formattedCreatedAt}
          </Typography>

          {isEditing ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1 }}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleUpdate();
                  }
                }}
              />
              <Box display="flex" gap={1} mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedContent(comment.content);
                  }}
                >
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleUpdate}>
                  Save
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{comment.content}</Typography>
              <Box display="flex" gap={2} mt={1}>
                {comment.createdBy === currentUser?.id && (
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Typography>
                )}
                {(comment.createdBy === currentUser?.id || isAdmin) && (
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setShowDeleteConfirmation(true)}
                  >
                    Delete
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => (setParentIsReplying ?? setIsReplying)(true)}
                >
                  Reply
                </Typography>
              </Box>
            </>
          )}
          {/* 🔁 Render nested replies */}
          {depth === 0 && comment.replies?.length > 0 && (
            <Box mt={2}>
              {comment.replies.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  entityId={entityId}
                  entityType={entityType}
                  refetchComments={refetchComments}
                  setParentIsReplying={depth === 0 ? setIsReplying : setParentIsReplying}
                  depth={depth + 1}
                />
              ))}
            </Box>
          )}

          {depth === 0 && isReplying && (
            <Box mt={2}>
              <TextField
                fullWidth
                multiline
                autoFocus
                minRows={2}
                maxRows={5}
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => (setParentIsReplying ?? setIsReplying)(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleReply}
                  disabled={!replyContent.trim()}
                >
                  Reply
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
      {depth === 0 && <Divider sx={{ mt: 2 }} />}
      {showDeleteConfirmation && (
        <ConfirmationDialog
          title="Delete Comment?"
          confirmLabel="Delete"
          message="Are you sure you want to delete this comment? This action cannot be undone."
          close={() => setShowDeleteConfirmation(false)}
          confirm={handleDelete}
        />
      )}
    </Box>
  );
};

export default Comment;
